import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { RouterLink } from '@angular/router';
import {
  ConsolidatedDto,
  DebtsOverNetWorthLimitsDto,
  PartnerCardDto,
} from '@interfaces/partner-income-tax/responses/partner-income-tax.response';
import { SharedModule } from '@shared/shared.module';
import { PARAMETRIZATION_ROUTE } from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/partner-income-tax.constants';
import {
  ConsolidatedSectionKey,
  PartnerIncomeTaxSectionState,
} from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/partner-income-tax-section.state';
import { ComparisonTableComponent } from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/components/comparison-table/comparison-table.component';
import { CoverageCardComponent } from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/components/coverage-card/coverage-card.component';
import { DataRoomDocumentsStore } from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/components/data-room-documents/data-room-documents.store';
import { DocumentsSectionComponent } from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/components/documents-section/documents-section.component';
import { FindingListComponent } from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/components/finding-list/finding-list.component';
import { DebtBarComponent } from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/components/kpi-visuals/debt-bar.component';
import { SparklineComponent } from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/components/kpi-visuals/sparkline.component';
import {
  SectionTab,
  SectionTabsComponent,
  documentsTab,
} from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/components/section-tabs/section-tabs.component';
import {
  debtLevelFromLimits,
  describeGroupConclusion,
  describeNetWorthChange,
  formatCompactMoney,
  formatRatio,
  signTone,
} from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/utils/partner-income-tax-format.util';

/** Ponto "dívidas sobre o patrimônio": o nível dele dá a cor do KPI de dívidas e a faixa cheia da barra. */
const DEBTS_OVER_NET_WORTH_POINT = 'IRS-D1';

/**
 * Visão consolidada no layout do protótipo 01: herói com a conclusão do grupo, KPIs, pontos do grupo e as abas
 * internas Comparativo entre sócios, Garantias por sócio (com política) e Documentos. Sem o campo "Garantia exigida"
 * desde a revisão de 29/10/2026: a exigência depende da operação estruturada, não do IR dos sócios.
 */
@Component({
  selector: 'app-pit-consolidated-view',
  standalone: true,
  imports: [
    RouterLink,
    SharedModule,
    FindingListComponent,
    SparklineComponent,
    DebtBarComponent,
    SectionTabsComponent,
    ComparisonTableComponent,
    CoverageCardComponent,
    DocumentsSectionComponent,
  ],
  templateUrl: './consolidated-view.component.html',
  styleUrl: './consolidated-view.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConsolidatedViewComponent {
  protected readonly sections = inject(PartnerIncomeTaxSectionState);
  private readonly documents = inject(DataRoomDocumentsStore);

  readonly consolidated = input.required<ConsolidatedDto>();
  readonly policyDefined = input(true);
  /** Limites de dívida/PL do sócio já carregado, quando o consolidado não traz os dele. */
  readonly fallbackDebtLimits = input<DebtsOverNetWorthLimitsDto | null | undefined>(null);
  /** Cards dos sócios do overview (selo e conclusão de cada um), para o herói do grupo. */
  readonly partners = input<PartnerCardDto[]>([]);

  readonly openPartner = output<string>();

  protected readonly parametrizationRoute = PARAMETRIZATION_ROUTE;
  protected readonly money = formatCompactMoney;
  protected readonly ratio = formatRatio;
  protected readonly tone = signTone;

  /** Abas do consolidado: a cobertura só existe com política de garantias. */
  private readonly sectionKeys = computed<ConsolidatedSectionKey[]>(() =>
    this.policyDefined() ? ['comparison', 'coverage', 'documents'] : ['comparison', 'documents']
  );
  protected readonly section = computed(() => this.sections.resolve(this.sectionKeys()));
  protected readonly tabs = computed<SectionTab[]>(() =>
    this.sectionKeys().map(key =>
      key === 'documents'
        ? documentsTab(this.documents.total(), this.documents.pending().length)
        : {
            key,
            title: key === 'comparison' ? 'Comparativo entre sócios' : 'Garantias por sócio',
          }
    )
  );

  /**
   * Conclusão do herói: o pior nível entre os sócios e de onde vem, pelo selo e pela conclusão que cada sócio já traz
   * no overview (nada calculado). Os pontos do GRUPO ficam só no card de pontos, embaixo.
   */
  protected readonly conclusion = computed(() => describeGroupConclusion(this.partners()));

  /**
   * Vazio dos pontos do grupo (R4-6): com sócio em Crítico ou Atenção no herói, a frase explica que pontos do grupo
   * são outra coisa, para não parecer que desmente o herói.
   */
  protected readonly groupEmptyText = computed(() =>
    this.partners().some(partner => partner.badge === 'critico' || partner.badge === 'atencao')
      ? 'Nenhum ponto que dependa de mais de um sócio. Os pontos de cada sócio estão na aba dele.'
      : 'Nenhum ponto de atenção neste ano.'
  );

  protected readonly partnerCount = computed(
    () => this.partners().length || this.consolidated().comparison.length
  );

  /** Anos comparáveis (com total do backend), para a média anual não repetir o número do ano (A6). */
  private readonly comparableYears = computed(
    () => (this.consolidated().netWorthTotalByYear ?? []).filter(item => item.value != null).length
  );

  protected readonly netWorthNote = computed(() => {
    const c = this.consolidated();
    return describeNetWorthChange(
      c.netWorthYoY,
      c.netWorthCagr,
      c.firstYear,
      this.comparableYears()
    );
  });

  /** Série do PL somado para a linha do KPI: os totais do backend, só nos anos comparáveis. */
  protected readonly netWorthSeries = computed(() =>
    (this.consolidated().netWorthTotalByYear ?? [])
      .filter(item => item.value != null)
      .sort((a, b) => a.year - b.year)
      .map(item => ({ year: item.year, value: item.value }))
  );

  /** Limites das faixas da barra dívida/PL: os do consolidado; sem eles, os do sócio carregado (mesma Parametrização). */
  protected readonly debtLimits = computed(
    () => this.consolidated().debtsOverNetWorthLimits ?? this.fallbackDebtLimits() ?? null
  );

  /**
   * Nível de dívida/PL do grupo (cor da nota e faixa cheia da barra): o do ponto IRS-D1 do grupo quando o backend
   * manda; senão, a faixa em que o valor cai pelos limites da Parametrização (N17).
   */
  protected readonly debtsLevel = computed(() => {
    const c = this.consolidated();
    const fromFinding = c.findings.find(finding =>
      finding.pointIds.includes(DEBTS_OVER_NET_WORTH_POINT)
    )?.level;
    return fromFinding ?? debtLevelFromLimits(c.debtsOverNetWorth, this.debtLimits());
  });
}
